import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { segmentationType } from '@bingads-webui-campaign/segmentation-utilities';

import { AdvisorChart } from './advisor-chart';
import {
  CHART_TYPES,
  DEVICE_TYPE,
  METRICS,
} from './consts';

const maxNumber = 5;

function getDeviceTypeList(raw, primaryDeviceType) {
  // get segmentData from first item
  const segmentData = raw[0].PerformanceMetrics.SegmentedData;

  if (_.size(segmentData) > 0 && _.size(segmentData[0].Key) > 1) {
    const keys = _.pluck(segmentData[0].Key, 'StringValue');

    if (_.intersection(keys, DEVICE_TYPE).length > 0) {
      // Move the primaryDeviceType to start of the list
      // Use compact to remove null from list
      return _.compact(_.union(
        [primaryDeviceType],
        _.values(DEVICE_TYPE)
      ));
    }
  }

  return [null];
}

function isYou(item) {
  return item.DisplayDomain === 'You';
}

function findYou(items) {
  return _.find(items, isYou);
}

function filterItems(raw, domainFilter) {
  const you = findYou(raw);
  let topItems = raw;

  if (!_.isEmpty(domainFilter)) {
    topItems = _.filter(raw, ({ DisplayDomain }) => _.contains(domainFilter, DisplayDomain));
  }

  // Ensure 'You' is always first item in chart
  topItems = _.first(topItems, maxNumber);

  if (you) {
    topItems = _.union([you], topItems);
    topItems = _.first(topItems, maxNumber);
  }

  return topItems;
}

function extractSegmentDataForMetric(items, metric) {
  let primarySegmentType = null;
  let secondarySegmentType = null;
  const data = _.map(items, (item) => {
    const keys = _.size(item.Key);
    let primarySegment = null;
    let secondarySegment = null;

    if (keys === 2) {
      const primary = _.find(
        item.Key,
        ({ SegmentationType }) => SegmentationType !== segmentationType.Device
      );
      primarySegment = primary.StringValue;
      primarySegmentType = primary.SegmentationType;

      const secondary = _.find(
        item.Key,
        ({ SegmentationType }) => SegmentationType === segmentationType.Device
      );

      secondarySegment = secondary.StringValue;
      secondarySegmentType = secondary.SegmentationType;
    } else {
      primarySegment = item.Key[0].StringValue;
      primarySegmentType = item.Key[0].SegmentationType;
    }

    return {
      primarySegment,
      secondarySegment,
      value: item.Data[metric],
    };
  });

  return {
    primarySegmentType,
    secondarySegmentType,
    data,
  };
}

function extractDataForMetric(topItems, metric) {
  let primarySegmentType = null;
  let secondarySegmentType = null;
  const shouldHideYou = _.contains([
    METRICS.AboveRate,
    METRICS.OverlapRate,
    METRICS.OutrankingShare,
  ], metric);

  const items = _.map(topItems, (item) => {
    const data = {
      name: item.DisplayDomain,
      data: [item.PerformanceMetrics[metric]],
    };

    if (shouldHideYou && isYou(item)) {
      data.visible = false;
    }

    const segmentData = extractSegmentDataForMetric(item.PerformanceMetrics.SegmentedData, metric);
    ({ primarySegmentType, secondarySegmentType } = segmentData);

    return _.extend(data, {
      segmentData: segmentData.data,
    });
  });

  return {
    items,
    primarySegmentType,
    secondarySegmentType,
  };
}

export class AdvisorChartWithMetrics extends React.PureComponent {
  static defaultProps = {
    chartType: null,
    domains: null,
    primaryDeviceType: null,
    onChartRenderedCallback: _.noop,
  };

  static propTypes = {
    i18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    selectedMetric: PropTypes.string.isRequired,
    primaryDeviceType: PropTypes.string,
    domains: PropTypes.arrayOf(PropTypes.string),
    chartType: PropTypes.oneOf(_.values(CHART_TYPES)),
    items: PropTypes.arrayOf(PropTypes.shape({
      PerformanceMetrics: PropTypes.object.isRequired,
      DisplayDomain: PropTypes.string.isRequired,
    })).isRequired,
    onChartRenderedCallback: PropTypes.func,
  };

  render() {
    if (_.isEmpty(this.props.items)) {
      return (null);
    }
    const deviceTypeList = getDeviceTypeList(this.props.items, this.props.primaryDeviceType);
    const topData = filterItems(this.props.items, this.props.domains);
    const {
      items,
      primarySegmentType,
    } = extractDataForMetric(topData, this.props.selectedMetric);
    const showDeviceTypeDropdown = _.size(deviceTypeList) > 1;

    return (
      <div className="advisor-metric-chart">
        <div className="row">
          <div className={showDeviceTypeDropdown ? 'col-sm-8' : 'col-sm-12'}>
            <AdvisorChart
              i18n={this.props.i18n}
              selectedMetric={this.props.selectedMetric}
              primarySegmentType={primarySegmentType}
              secondarySegment={deviceTypeList[0]}
              type={this.props.chartType}
              items={items}
              onChartRenderedCallback={this.props.onChartRenderedCallback}
            />
          </div>
          {showDeviceTypeDropdown &&
            <div className="col-sm-4">
              <div className="row">
                <div className="col-sm-12">
                  <AdvisorChart
                    i18n={this.props.i18n}
                    selectedMetric={this.props.selectedMetric}
                    primarySegmentType={primarySegmentType}
                    secondarySegment={deviceTypeList[1]}
                    type={CHART_TYPES.Secondary}
                    items={items}
                    enableLegend={false}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <AdvisorChart
                    i18n={this.props.i18n}
                    selectedMetric={this.props.selectedMetric}
                    primarySegmentType={primarySegmentType}
                    secondarySegment={deviceTypeList[2]}
                    type={CHART_TYPES.Secondary}
                    items={items}
                    enableLegend={false}
                  />
                </div>
              </div>
            </div>
          }
        </div>
      </div >
    );
  }
}
